import { ReactNode } from 'react'
import { Box } from '@mui/material'

interface MiniProgressDotProps {
  // The metric value (0–100). Pass `null` / `undefined` to render the
  // neutral "not generated yet" state — a literal `0` is a real result
  // and shows the bottom-of-band colour (red on a normal palette),
  // so callers must distinguish "no data" from "scored zero" before
  // passing this in.
  value: number | null | undefined
  icon: ReactNode
  label?: string         // shown in the tooltip prefix, e.g. "Trace"
  scale?: number         // 1 = 18px circle
  reverse?: boolean      // flip color bands (high = bad)
}

// Single-stop bands aligned with FancyProgressArc's gradients (we just take
// the start color so a tiny solid dot still reads at a glance).
type Band = [minVal: number, color: string]

const BANDS_NORMAL: Band[] = [
  [80, '#2E8B57'],
  [60, '#68AC86'],
  [40, '#FF8C00'],
  [20, '#DC5B74'],
  [0, '#DC143C'],
]
const BANDS_REVERSE: Band[] = [
  [80, '#DC143C'],
  [60, '#DC5B74'],
  [40, '#FF8C00'],
  [20, '#68AC86'],
  [0, '#2E8B57'],
]

const NULL_COLOR = '#e7e7e8'   // matches FancyProgressArc's neutral track

function bandColor(value: number, reverse: boolean): string {
  const bands = reverse ? BANDS_REVERSE : BANDS_NORMAL
  for (const [min, color] of bands) {
    if (value > min) return color
  }
  return bands[bands.length - 1][1]
}

// A tiny circular indicator that works where FancyProgressArc is too detailed
// (e.g. dense list rows). Filled with the value's band color and the supplied
// icon centred on top; falls back to a grey circle with a muted icon when the
// value is zero / unpopulated.
export default function MiniProgressDot({
  value, icon, scale = 1, reverse = false,
}: MiniProgressDotProps) {
  // "Not generated" is signalled by null / undefined. An explicit `0`
  // is a real scored result — render the band colour for 0 (the
  // bottom band, red on a normal palette) so the user can tell the
  // difference between "score=0" and "no score yet".
  const isNull = value == null
  const color = isNull ? NULL_COLOR : bandColor(value as number, reverse)
  const size = Math.round(18 * scale)
  // const tooltip = label
  //   ? `${label}: ${isZero ? 'n/a' : `${Math.round(value)}%`}`
  //   : isZero ? 'n/a' : `${Math.round(value)}%`

  const dot = (
    <Box sx={{
      width: size, height: size,
      borderRadius: '50%',
      bgcolor: color,
      display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      color: isNull ? 'text.secondary' : '#fff',
      boxShadow: isNull ? 'inset 0 0 0 1px rgba(0,0,0,0.08)' : 'none',
    }}>
      {icon}
    </Box>
  )

  // return <Tooltip title={tooltip}>{dot}</Tooltip>
  return dot
}
